import React, { useState } from 'react'
import { AlertItem, Severity } from '../core/types'
import { OVERALL_TITLE, SEVERITY_COLOR, SEVERITY_RANK } from '../core/consts'
import { ALERT_HINTS } from '../core/hints'
import InfoHint from '../../../../../common/InfoHint'

type Props = {
  severity: Severity
  alerts: AlertItem[]
  onOpenDrilldown: (
    signal: string,
    title: string,
    filters?: { currency?: string; merchant?: number }
  ) => void
  onOpenMetrics: () => void
}

// The only clickable element in a row: subtle underlined "Смотреть →" that
// brightens on hover and nudges on press (arrow + text underline, no brackets).
const WatchLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [pressed, setPressed] = useState(false)
  return (
    <button
      type='button'
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      className='btn btn-link btn-sm p-0 ms-3 text-gray-700 text-hover-primary text-decoration-underline fw-semibold'
      style={{ transform: pressed ? 'translateY(1px)' : 'none', whiteSpace: 'nowrap' }}
    >
      Смотреть →
    </button>
  )
}

const HealthBanner: React.FC<Props> = ({ severity, alerts, onOpenDrilldown, onOpenMetrics }) => {
  const color = SEVERITY_COLOR[severity]
  const sorted = [...alerts].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity])

  return (
    <div className={`card mb-5 bg-light-${color} border border-${color}`}>
      <div className='card-body py-5'>
        {/* Title and the metrics entry point share a row: the banner answers
            "is anything wrong", the button answers "show me everything". */}
        <div
          className={`d-flex flex-wrap align-items-center justify-content-between gap-3 mb-${
            sorted.length ? '4' : '0'
          }`}
        >
          <h2 className={`m-0 text-${color} d-inline-flex align-items-center`}>
            {OVERALL_TITLE[severity]}
            <InfoHint text={ALERT_HINTS.overall} />
          </h2>
          <button
            type='button'
            onClick={onOpenMetrics}
            className='btn btn-sm btn-light border border-gray-300 fw-semibold'
          >
            Все метрики
          </button>
        </div>

        {sorted.length > 0 && (
          <div className='d-flex flex-column gap-2'>
            {sorted.map((a) => {
              const clickable = a.drilldown !== ''
              return (
                <div key={a.code} className='d-flex align-items-center'>
                  {/* severity conveyed by a colored dot, not a word */}
                  <span
                    className={`bullet bullet-dot bg-${
                      SEVERITY_COLOR[a.severity]
                    } h-8px w-8px me-3 flex-shrink-0`}
                    title={a.severity}
                  />
                  {typeof a.values?.currency === 'string' && (
                    <span className='badge badge-light-dark text-gray-800 me-2'>
                      {a.values.currency as string}
                    </span>
                  )}
                  <span className='fw-semibold text-gray-900'>{a.title}</span>
                  {ALERT_HINTS[a.code] && <InfoHint text={ALERT_HINTS[a.code]} />}
                  {a.values?.trend === 'up' && (
                    <i className='bi bi-arrow-up-right text-danger ms-1' title='растёт' />
                  )}
                  {a.values?.trend === 'down' && (
                    <i className='bi bi-arrow-down-right text-success ms-1' title='снижается' />
                  )}
                  {a.detail && <span className='text-gray-800 ms-2'>— {a.detail}</span>}
                  {clickable && (
                    <WatchLink
                      onClick={() =>
                        onOpenDrilldown(a.drilldown, a.title, {
                          currency:
                            typeof a.values?.currency === 'string' ? a.values.currency : undefined,
                          merchant:
                            typeof a.values?.merchant_id === 'number'
                              ? a.values.merchant_id
                              : undefined,
                        })
                      }
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default HealthBanner
